import React from 'react';
import Head from 'next/head';
import Script from 'next/script';

import config from '../../../config';
import { usePathname } from 'next/navigation';
import { rewriteImgixToImageCdn } from '../../constants/imageCdn';

const SITE_URL = config.baseUri;

interface MetaProps {
  title: string;
  description: string;
  imageSrc?: string;
  path: string;
  type?: string;
  schemaType?: 'Product' | 'LocalBusiness';
  averageRating?: number;
  reviewCount?: number;
}

const Meta = ({
  title,
  description,
  imageSrc = `https://images.prismic.io/togather-public/6865703a-ad7c-42a1-837c-eb4a7755f79b_Meta+Image.png`,
  path,
  type = 'website',
  averageRating = 4.9,
  schemaType = 'LocalBusiness',
  reviewCount = 680,
}: MetaProps) => {
  const pathname = usePathname();
  const url = path ?? `${SITE_URL}/listings${pathname}`;
  const formattedImageSrc = rewriteImgixToImageCdn(imageSrc);

  const reviewSchema = `
    {
      "@context": "http://schema.org",
      "@type": "${schemaType}",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "${averageRating}",
        "reviewCount": "${reviewCount}"
      },
      "name": "${title}",
      "description": "${description}",
      "url": "${url}",
      ${
        schemaType !== 'Product'
          ? `"currenciesAccepted": "GBP",
            "paymentAccepted": "Credit Card, Debit Card, PayPal",
            "openingHours": "Mo-Su",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "London",
              "addressCountry": "GB",
              "postalCode": "E1 5JL",
              "streetAddress": "68-80 Hanbury Street"
            },`
          : ''
      }
      "sameAs": [
        "https://www.facebook.com/togathereventsuk",
        "https://twitter.com/togatherevents",
        "https://uk.linkedin.com/company/togatherevents",
        "https://www.instagram.com/togatherevents_/"
      ],
      "image": [
        "${formattedImageSrc}"
      ]
      ${
        schemaType === 'Product'
          ? `,
        "brand": {
          "@type": "Service",
          "name": "Togather"
        }
      `
          : ''
      }
    }
  `;

  return (
    <>
      <Head>
        <title>{title}</title>

        {config.noIndexNoFollow && (
          <meta name="robots" content="noindex, nofollow" />
        )}

        <meta name="description" content={description} />

        <meta property="og:title" content={title} />

        <meta property="og:description" content={description} />

        <meta property="og:image" content={formattedImageSrc} />

        <meta property="og:type" content={type} />

        <meta property="og:url" content={url} />

        <meta
          name="facebook-domain-verification"
          content="rhl9vedef2uwjuhubv5kcxzmddp1kf"
        />

        <link rel="canonical" href={url} />

        <link rel="alternate" hrefLang="en-gb" href={url} />

        {/* Preload hero image for LCP optimization */}
        {formattedImageSrc && (
          <link
            rel="preload"
            as="image"
            href={formattedImageSrc}
            fetchPriority="high"
          />
        )}
      </Head>

      {config.enableSchemas && (
        <Script
          id="structured-data-script"
          key={`structured-data-script-${url}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: reviewSchema }}
        />
      )}
    </>
  );
};

export default Meta;
